import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Avatar,
  Stack,
  Paper,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Breadcrumbs,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Verified as VerifiedIcon,
  ArrowBack as ArrowBackIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Home as HomeIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { Company, CompanyMember } from '../types';
import { CompanyService } from '../services/companyService';
import { ProductService } from '../services/productService';
import ProductCard from '../components/Product/ProductCard';
import { useTheme } from '../hooks/useTheme';
import { useUIStore } from '../store/uiStore';

const CompanyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    jobApplicationWriterMinimized, 
    setJobApplicationWriterOpen, 
    addSelectedJob, 
    removeSelectedJob, 
    isJobSelected 
  } = useUIStore();
  
  // Check if the parameter is a UUID (ID) or a slug
  const param = id;
  const isUuid = param && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(param);
  const { currentTheme } = useTheme();
  const [company, setCompany] = useState<Company | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'jobs' | 'team' | 'reviews'>('overview');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [companyMembersCount, setCompanyMembersCount] = useState<number>(0);
  const [memberIds, setMemberIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!param) {
        console.log('❌ No company route param provided');
        setError('Company not found');
        setLoading(false);
        return;
      }

      console.log('🚀 Starting to fetch company data for param:', param);

      try {
        setLoading(true);
        setError(null);

        // Test database connection first
        console.log('🔌 Testing database connection...');
        const isConnected = await CompanyService.testConnection();
        if (!isConnected) {
          throw new Error('Unable to connect to the database. Please check your connection and try again.');
        }

        // Debug companies table
        await CompanyService.debugCompanies();

        // Fetch company data
        console.log('📡 Fetching company data...');
        console.log('Parameter:', param, 'Is UUID:', isUuid);
        
        let companyData: Company | null = null;
        if (isUuid) {
          // If it's a UUID, fetch by ID
          console.log('Fetching company by ID:', param);
          companyData = await CompanyService.getCompanyById(param);
        } else {
          // If it's a slug, fetch by slug
          console.log('Fetching company by slug:', param);
          companyData = await CompanyService.getCompanyBySlug(param);
        }
        
        if (!companyData) {
          console.log('❌ No company data returned for parameter:', param);
          setError('Company not found');
          return;
        }

        console.log('✅ Company data fetched successfully:', {
          id: companyData.id,
          name: companyData.name,
          slug: companyData.slug,
          profileViews: companyData.profileViews,
          hasProfileViews: 'profileViews' in companyData
        });

        // The company data is already properly mapped in the service
        const safeCompanyData = companyData;

        setCompany(safeCompanyData);

        // Debug company members first
        const debugInfo = await CompanyService.debugCompanyMembers(safeCompanyData.id);
        setCurrentUserId(debugInfo.currentUserId);
        setCompanyMembersCount(debugInfo.membersCount);
        setMemberIds(debugInfo.memberIds);

        // Check if current user is admin of this company
        console.log('🔐 Checking admin status for company:', {
          companyId: safeCompanyData.id,
          companyName: safeCompanyData.name,
          companySlug: safeCompanyData.slug,
          currentUserId: debugInfo.currentUserId,
          membersCount: debugInfo.membersCount
        });
        try {
          const adminStatus = await CompanyService.isUserAdminOfCompany(safeCompanyData.id);
          setIsAdmin(adminStatus);
          console.log('✅ Admin status checked:', {
            isAdmin: adminStatus,
            companyId: safeCompanyData.id,
            companyName: safeCompanyData.name,
            currentUserId: debugInfo.currentUserId,
            membersCount: debugInfo.membersCount
          });
        } catch (adminError) {
          console.warn('⚠️ Failed to check admin status:', adminError);
          setIsAdmin(false);
        } finally {
          setCheckingAdmin(false);
        }

        // Increment profile views
        console.log('📊 Incrementing profile views...');
        try {
          await CompanyService.incrementProfileViews(companyData.id);
          console.log('✅ Profile views incremented');
        } catch (viewError) {
          console.warn('⚠️ Failed to increment profile views:', viewError);
          // Don't fail the whole process for this
        }

        // Fetch company jobs (stored as products with is_job=true)
        console.log('💼 Fetching company jobs (products.is_job)...');
        try {
          const { products } = await ProductService.getProductsWithSellers({
            page: 1,
            limit: 60,
            isActive: true,
            isJob: true,
            companyId: companyData.id,
            sortBy: 'created_at',
            sortOrder: 'desc',
          });
          console.log('✅ Company jobs fetched:', products.length);
          setJobs(products || []);
        } catch (jobsError) {
          console.warn('⚠️ Failed to fetch company jobs:', jobsError);
          setJobs([]);
        }

        // Fetch company members
        console.log('👥 Fetching company members...');
        try {
          const membersData = await CompanyService.getCompanyMembers(companyData.id);
          console.log('✅ Company members fetched:', membersData.length);
          setMembers(membersData);
        } catch (membersError) {
          console.warn('⚠️ Failed to fetch company members:', membersError);
          setMembers([]); // Set empty array instead of failing
        }

        console.log('🎉 All company data fetched successfully!');

      } catch (err) {
        console.error('💥 Error fetching company data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load company data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [param]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography color="text.secondary">Loading company...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
            {error || 'Company not found'}
          </Typography>
          <Button
            component={Link}
            to="/companies"
            variant="outlined"
            startIcon={<HomeIcon />}
          >
            Back to Companies
          </Button>
        </Box>
      </Box>
    );
  }

  const formatSalary = (min?: number, max?: number, currency = 'USD', period = 'yearly') => {
    if (!min && !max) return 'Salary not specified';
    
    const formatNumber = (num: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    };

    if (min && max) {
      return `${formatNumber(min)} - ${formatNumber(max)} per ${period}`;
    } else if (min) {
      return `${formatNumber(min)}+ per ${period}`;
    } else if (max) {
      return `Up to ${formatNumber(max)} per ${period}`;
    }
  };

  const transformJobForCard = (p: any) => {
    const companyName = String(p?.company_join?.name || company?.name || 'Company');
    const companyLogo = String(p?.company_join?.logo_url || company?.logoUrl || '');
    const locationName = String(p?.location_name || p?.location || company?.headquartersCity || '');
    const salaryMin = typeof p?.salary_min === 'number' ? p.salary_min : undefined;
    const salaryMax = typeof p?.salary_max === 'number' ? p.salary_max : undefined;
    const salaryCurrency = String(p?.salary_currency || 'USD');

    const createdAt = String(p?.created_at || new Date().toISOString());
    const isNew = (() => {
      try {
        const d = new Date(createdAt);
        const now = new Date();
        const diffDays = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
        return diffDays <= 7;
      } catch {
        return false;
      }
    })();

    const normalizeExperienceLevel = (raw: any) => {
      const vRaw = String(raw || '').trim();
      const v = vRaw.toLowerCase();
      if (!v) return 'mid';
      // Keep the same label style as All Jobs (e.g. "entry-level")
      if (v.includes('entry') || v.includes('junior')) return v.includes('level') ? vRaw : 'entry-level';
      if (v.includes('mid')) return vRaw;
      if (v.includes('senior')) return vRaw;
      if (v.includes('exec')) return v.includes('level') ? vRaw : 'executive';
      return vRaw;
    };

    const requiredSkills = Array.isArray(p?.required_skills)
      ? p.required_skills
      : Array.isArray(p?.requiredSkills)
        ? p.requiredSkills
        : Array.isArray(p?.job_requirements)
          ? p.job_requirements
          : Array.isArray(p?.requirements)
            ? p.requirements
            : [];

    const benefits = Array.isArray(p?.benefits_perks)
      ? p.benefits_perks
      : Array.isArray(p?.benefits)
        ? p.benefits
        : [];

    const applicantsCount = (() => {
      const v = Number(p?.application_count ?? p?.applicantsCount ?? 0);
      if (Number.isFinite(v) && v > 0) return v;
      return Math.floor(Math.random() * 50);
    })();

    const viewsCount = (() => {
      const v = Number(p?.view_count ?? p?.viewsCount ?? 0);
      if (Number.isFinite(v) && v > 0) return v;
      return Math.floor(Math.random() * 200);
    })();

    return {
      id: String(p?.id || ''),
      name: String(p?.name || 'Untitled'),
      price: Number(p?.price || 0),
      image: Array.isArray(p?.images) && p.images.length > 0 ? p.images[0] : '/placeholder-product.jpg',
      rating: 0,
      reviewCount: 0,
      category: String(p?.category_join?.name || 'Jobs'),
      seller: {
        name: companyName,
        avatar: companyLogo,
        location: locationName,
      },
      isNew,
      isFeatured: Boolean(p?.is_featured),
      description: String(p?.description || ''),
      tags: Array.isArray(p?.tags) ? p.tags : [],
      isJob: true,
      company: companyName,
      companyLogo,
      location: locationName,
      salary: salaryMin && salaryMax ? { min: salaryMin, max: salaryMax, currency: salaryCurrency } : undefined,
      employmentType: String(p?.job_type || p?.employment_type || 'full-time'),
      experienceLevel: normalizeExperienceLevel(p?.experienceLevel ?? p?.experience_level),
      isRemote: Boolean(p?.remote_work_allowed || p?.is_remote),
      postedDate: createdAt,
      requirements: requiredSkills,
      benefits,
      applicantsCount,
      viewsCount,
      duration: String(p?.duration || ''),
      sellerId: String(p?.seller_id || ''),
    };
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Company Header */}
      <Box sx={{ position: 'relative' }}>
        {/* Cover Image */}
        {company.coverImageUrl && (
          <Box
            sx={{
              height: 256,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              component="img"
              src={company.coverImageUrl}
              alt={`${company.name} cover`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        )}
        
        {/* Company Info */}
        <Paper sx={{ shadow: 3, borderRadius: 0 }}>
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: 3 }}>
              {/* Logo */}
              <Box sx={{ flexShrink: 0 }}>
                <Avatar
                  src={company.logoUrl}
                  sx={{
                    width: 96,
                    height: 96,
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    border: 4,
                    borderColor: 'background.paper',
                    boxShadow: 3
                  }}
                >
                  {company.logoUrl ? null : company.name.charAt(0).toUpperCase()}
                </Avatar>
              </Box>

              {/* Company Details */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
                        {company.name}
                      </Typography>
                      {company.isVerified && (
                        <Chip
                          icon={<VerifiedIcon />}
                          label="Verified"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>
                    {company.tagline && (
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        {company.tagline}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
                      {company.industry && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Industry:</strong> {company.industry}
                          </Typography>
                        </Box>
                      )}
                      {company.companySize && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Size:</strong> {company.companySize}
                          </Typography>
                        </Box>
                      )}
                      {company.headquartersCity && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Location:</strong> {company.headquartersCity}
                            {company.headquartersCountry && `, ${company.headquartersCountry}`}
                          </Typography>
                        </Box>
                      )}
                      {company.isRemoteFriendly && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PublicIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main">
                            <strong>Remote Friendly</strong>
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Action Buttons */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ mt: { xs: 2, sm: 0 }, alignItems: { sm: 'center' } }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => {
                        if (typeof window !== 'undefined' && window.history.length > 1) {
                          navigate(-1);
                          return;
                        }
                        navigate('/companies');
                      }}
                      sx={{
                        borderRadius: 999,
                        px: 2.5,
                        py: 1.1,
                        textTransform: 'none',
                        fontWeight: 900,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Zurück
                    </Button>
                    {!checkingAdmin && isAdmin && (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/companies/${company.slug}/edit`)}
                        sx={{
                          borderRadius: 999,
                          px: 2.5,
                          py: 1.1,
                          textTransform: 'none',
                          fontWeight: 900,
                          background: 'linear-gradient(135deg, #ff7a18 0%, #ff3d00 100%)',
                          boxShadow: '0 10px 22px rgba(255, 61, 0, 0.22)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #ff6a00 0%, #e63900 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 14px 28px rgba(255, 61, 0, 0.26)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Edit Company
                      </Button>
                    )}

                    {company.website && (
                      <Button
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<LanguageIcon />}
                        sx={{
                          borderRadius: 999,
                          px: 2.5,
                          py: 1.1,
                          textTransform: 'none',
                          fontWeight: 900,
                          borderWidth: 2,
                          background: 'rgba(255,255,255,0.7)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            borderWidth: 2,
                            background: 'rgba(255,255,255,0.9)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Visit Website
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      startIcon={<StarIcon />}
                      sx={{
                        borderRadius: 999,
                        px: 2.5,
                        py: 1.1,
                        textTransform: 'none',
                        fontWeight: 900,
                        background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
                        color: '#fff',
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#fff' },
                        boxShadow: '0 10px 22px rgba(17, 24, 39, 0.22)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0b1220 0%, #1f2937 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 14px 28px rgba(17, 24, 39, 0.26)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Follow Company
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Container>
        </Paper>
      </Box>

      {/* Navigation Tabs */}
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="xl">
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 900,
                minHeight: 48,
                borderRadius: 999,
                px: 2,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, rgba(255, 122, 24, 0.14) 0%, rgba(255, 61, 0, 0.10) 100%)',
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ff7a18 0%, #ff3d00 100%)',
              },
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label={`Jobs (${jobs.length})`} value="jobs" />
            <Tab label={`Team (${members.length})`} value="team" />
            <Tab label="Reviews" value="reviews" />
          </Tabs>
        </Container>
      </Paper>

      {/* Tab Content */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {activeTab === 'overview' && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4 }}>
            {/* Main Content */}
            <Box sx={{ flex: 2 }}>
              <Stack spacing={4}>
                {/* About */}
                {company.description && (
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                        About {company.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {company.description}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Mission Statement */}
                {company.missionStatement && (
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                        Mission Statement
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {company.missionStatement}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Values */}
                {company.values && company.values.length > 0 && (
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                        Our Values
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        {company.values.map((value, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary">
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Benefits & Perks */}
                {(company.benefits && company.benefits.length > 0) || (company.perks && company.perks.length > 0) && (
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                        Benefits & Perks
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        {company.benefits?.map((benefit, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'success.main', fontSize: '1.2rem' }}>✓</Box>
                            <Typography variant="body2" color="text.secondary">
                              {benefit}
                            </Typography>
                          </Box>
                        ))}
                        {company.perks?.map((perk, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'secondary.main', fontSize: '1.2rem' }}>🎁</Box>
                            <Typography variant="body2" color="text.secondary">
                              {perk}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>

            {/* Sidebar */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Stack spacing={3}>
                {/* Company Stats */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      Company Stats
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Founded</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {company.foundedYear || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Company Size</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {company.companySize || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Type</Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                          {company.companyType}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Profile Views</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {(company.profileViews || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Contact Info */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      Contact Information
                    </Typography>
                    <Stack spacing={2}>
                      {company.website && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Website
                          </Typography>
                          <Typography
                            component="a"
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            color="primary"
                            sx={{ display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {company.website}
                          </Typography>
                        </Box>
                      )}
                      {company.email && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography
                            component="a"
                            href={`mailto:${company.email}`}
                            variant="body2"
                            color="primary"
                            sx={{ display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {company.email}
                          </Typography>
                        </Box>
                      )}
                      {company.phone && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography
                            component="a"
                            href={`tel:${company.phone}`}
                            variant="body2"
                            color="primary"
                            sx={{ display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {company.phone}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Social Links */}
                {(company.linkedinUrl || company.twitterUrl || company.facebookUrl || company.instagramUrl) && (
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        Follow Us
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        {company.linkedinUrl && (
                          <Button
                            href={company.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<LinkedInIcon />}
                            size="small"
                            variant="outlined"
                          >
                            LinkedIn
                          </Button>
                        )}
                        {company.twitterUrl && (
                          <Button
                            href={company.twitterUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<TwitterIcon />}
                            size="small"
                            variant="outlined"
                          >
                            Twitter
                          </Button>
                        )}
                        {company.facebookUrl && (
                          <Button
                            href={company.facebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<FacebookIcon />}
                            size="small"
                            variant="outlined"
                          >
                            Facebook
                          </Button>
                        )}
                        {company.instagramUrl && (
                          <Button
                            href={company.instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<InstagramIcon />}
                            size="small"
                            variant="outlined"
                          >
                            Instagram
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {activeTab === 'jobs' && (
          <Stack spacing={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                Open Positions ({jobs.length})
              </Typography>
              {jobs.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Showing {jobs.length} job{jobs.length !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>

            {jobs.length === 0 ? (
              <Card>
                <CardContent sx={{ p: 8, textAlign: 'center' }}>
                  <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                    No open positions
                  </Typography>
                  <Typography color="text.secondary">
                    This company doesn't have any open positions at the moment.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2.5,
                }}
              >
                {jobs.map((job) => (
                  <ProductCard
                    key={job.id}
                    product={transformJobForCard(job)}
                    onViewDetails={(productId) => navigate(`/product/${productId}`)}
                    showSeller={false}
                    isOwnProduct={Boolean(currentUserId) && String((job as any)?.seller_id || '') === String(currentUserId)}
                  />
                ))}
              </Box>
            )}
          </Stack>
        )}

        {activeTab === 'team' && (
          <Stack spacing={4}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
              Team Members ({members.length})
            </Typography>

            {members.length === 0 ? (
              <Card>
                <CardContent sx={{ p: 8, textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                    No team members
                  </Typography>
                  <Typography color="text.secondary">
                    This company doesn't have any public team members.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                {members.map((member) => (
                  <Box key={member.id}>
                    <Card>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                            {member.user?.name?.charAt(0).toUpperCase() || '?'}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {member.user?.name || 'Unknown User'}
                            </Typography>
                            {member.jobTitle && (
                              <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {member.jobTitle}
                              </Typography>
                            )}
                            {member.department && (
                              <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {member.department}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                size="small"
                                color={
                                  member.role === 'owner' 
                                    ? 'secondary' 
                                    : member.role === 'admin'
                                    ? 'primary'
                                    : 'default'
                                }
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Stack>
        )}

        {activeTab === 'reviews' && (
          <Stack spacing={4}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
              Company Reviews
            </Typography>

            <Card>
              <CardContent sx={{ p: 8, textAlign: 'center' }}>
                <StarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                  No reviews yet
                </Typography>
                <Typography color="text.secondary">
                  Be the first to review this company.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default CompanyPage;
